import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react'

import { useDebouncedCallback } from './use-debounced-callback'

/** What a list that is fetched waits before asking again. */
const CHIP_DEBOUNCE_MS = 300

/**
 * What a filter chip's field holds while it is being typed into.
 *
 * The draft follows the keystroke and the parent follows a beat later, so a
 * filtered refetch does not fire per character. The parent's value is copied
 * back in only while the box is unfocused — a reset landing mid-word would
 * otherwise yank the caret.
 *
 * `delayMs` of `0` reports every keystroke instead, for a list filtered in the
 * browser: there is no request to spare, so a beat is only a delay.
 */
export function useChipDraft(
  value: string,
  onChange: (value: string) => void,
  delayMs = CHIP_DEBOUNCE_MS,
) {
  const [local, setLocal] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  const commit = useDebouncedCallback(onChange, delayMs)

  useEffect(() => {
    if (document.activeElement !== ref.current) setLocal(value)
  }, [value])

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value
      setLocal(next)
      if (delayMs === 0) onChange(next)
      else commit(next)
    },
    [commit, delayMs, onChange],
  )

  return { ref, value: local, onChange: onInputChange }
}
